import React, { useState, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Checkbox, DatePicker, Divider, Form, Icon, Input, Tooltip
} from 'antd';
import { setLowerDateRange, setUpperDateRange } from 'library/helpers';
import { canViewOptimiser, canAdminOptimiser } from 'library/optimiser';
import { FormWrapper } from 'components/Page';
import axios from 'axios';
import _ from 'lodash';
import { ApiEndpoints } from 'api/urls';
import MentorRankList from './MentorRankList';

const FormItem = Form.Item;

// `preferred_mentors` reaches this form in TWO shapes and every reader must tolerate both.
// The API returns [{ id, email, rank }], but FormWrapper.handleValuesChange merges the raw
// FORM value ([41, 87]) back into the same key on the item it feeds down as dataSource. So
// after the user's first edit the objects are gone. Assuming the API shape here re-derived
// [undefined], which rendered as the literal "undefined" and made Save fail with
// "preferred_mentors - Invalid preferred mentor id."
const mentorIdOf = m => (m && typeof m === 'object' ? m.id : m);
const mentorIdsOf = list => (list || []).map(mentorIdOf)
  .filter(id => id != null);

const OPTIMISER_LEGEND = <div style={{ textAlign: 'right', padding: '4px 0' }}><span style={{ fontSize: '11px', color: '#888', fontStyle: 'italic' }}>** = Used by Optimiser</span></div>;

const mapStateToProps = state => ({
  person: state.api.userAccount.data
});

const mapPropsToFields = (props) => {
  const { dataSource: data } = props;

  return {
    employee_number: Form.createFormField({ value: data.employee_number }),
    employer: Form.createFormField({ value: data.employer }),
    employee_position: Form.createFormField({ value: data.employee_position }),
    start_date: Form.createFormField({ value: data.start_date }),
    end_date: Form.createFormField({ value: data.end_date }),
    lms_id: Form.createFormField({ value: data.lms_id }),

    supervisor: Form.createFormField({ value: data.supervisor }),
    supervisor_contact_number: Form.createFormField({ value: data.supervisor_contact_number }),
    supervisor_email: Form.createFormField({ value: data.supervisor_email }),

    suburb: Form.createFormField({ value: data.suburb }),
    state: Form.createFormField({ value: data.state }),
    country: Form.createFormField({ value: data.country }),

    home_number: Form.createFormField({ value: data.home_number }),
    personal_email_address: Form.createFormField({ value: data.personal_email_address }),
    primary_phone_number: Form.createFormField({ value: data.primary_phone_number }),
    secondary_phone_number: Form.createFormField({ value: data.secondary_phone_number }),

    emergency_contact_name: Form.createFormField({ value: data.emergency_contact_name }),
    emergency_contact_relationship: Form.createFormField({ value: data.emergency_contact_relationship }),
    emergency_contact_home: Form.createFormField({ value: data.emergency_contact_home }),
    emergency_contact_mobile: Form.createFormField({ value: data.emergency_contact_mobile }),

    order: Form.createFormField({ value: data.order }),
    // Ordered list of preferred mentor ids, most preferred first. Always an array so
    // an emptied list reaches the backend as [] ("clear all") rather than being dropped.
    preferred_mentors: Form.createFormField({ value: mentorIdsOf(data.preferred_mentors) }),
    // Optimiser inclusion flag. Undefined (no backend value yet) defaults to checked/true.
    include_for_optimiser: Form.createFormField({ value: data.include_for_optimiser !== false })
  };
};

const onFieldsChange = (props, fields) => {
  const { onFieldsChange: dispatchOnFieldsChange } = props;
  if (dispatchOnFieldsChange) {
    dispatchOnFieldsChange(fields);
  }
};

const onValuesChange = (props, values) => {
  const { onValuesChange: dispatchOnValuesChange } = props;
  if (dispatchOnValuesChange) {
    dispatchOnValuesChange(values);
  }
};

const FormInput = ({
  canAdmin, dataSource: data, checkboxFormItemLayout, inputFormItemLayout, form, person
}) => {
  const [mentorOptions, setMentorOptions] = useState([]);
  const [mentorFetching, setMentorFetching] = useState(false);
  // id -> email for every mentor we have ever seen, so a selected mentor keeps its label.
  // Per-instance and therefore lost on a remount; that is fine because a fresh mount reads
  // dataSource from the API in {id, email, rank} shape and repopulates the cache below.
  const mentorLabelCache = useRef({});

  // Debounced search over all users in the current tenancy.
  // Called with no search term we show the first 5 rather than the whole list; that
  // no-term call comes from MentorRankList itself, which fires onSearch('') when its
  // dropdown opens with nothing to show, so the list is not empty on focus.
  const fetchMentors = useCallback(
    _.debounce((value) => {
      const params = value
        ? { user_search: 'true', search: value, limit: 20 }
        : { user_search: 'true', limit: 5 };
      setMentorFetching(true);
      axios.get(ApiEndpoints.userList, { params })
        .then((resp) => {
          // The global axios response interceptor wraps response.data in Immutable's fromJS,
          // so we need toJS() to get a plain JS object before reading .results.
          const payload = resp.data && resp.data.toJS ? resp.data.toJS() : (resp.data || {});
          setMentorOptions((payload.results || []).map(u => ({ value: u.id, label: u.email })));
        })
        .finally(() => setMentorFetching(false));
    }, 300),
    []
  );

  const { getFieldDecorator } = form;
  const readonly = !canAdmin;
  const managed = person && person.is_ad_user;
  // Optimiser-only controls: only render the optimiser fields when the current
  // user can view the Optimiser (tenancy Optimiser-active AND the user holds
  // can_view_optimiser/can_admin_optimiser for the current tenancy).
  const optimiserEnabled = canViewOptimiser();

  // Seed an option for every current mentor so their emails show before the user searches.
  // Labels are cached across renders because dataSource loses them: once FormWrapper feeds
  // back a bare id array, and once the next search replaces mentorOptions, the email for an
  // already-selected mentor exists nowhere else and the row would fall back to its raw id.
  (data.preferred_mentors || []).forEach((m) => {
    if (m && typeof m === 'object' && m.id != null && m.email) {
      mentorLabelCache.current[m.id] = m.email;
    }
  });
  mentorOptions.forEach((o) => {
    if (o.value != null && o.label) {
      mentorLabelCache.current[o.value] = o.label;
    }
  });
  const seededMentorOptions = mentorIdsOf(data.preferred_mentors)
    .filter(id => mentorLabelCache.current[id])
    .map(id => ({ value: id, label: mentorLabelCache.current[id] }));
  const mentorSelectOptions = [
    ...seededMentorOptions.filter(s => !mentorOptions.some(o => o.value === s.value)),
    ...mentorOptions
  ];
  return (
    <Form>
      <FormItem {...inputFormItemLayout} label="Employee No.">
        {
          getFieldDecorator('employee_number', {
            initialValue: data.employee_number
          })(<Input disabled={readonly || managed} />)
        }
      </FormItem>
      <FormItem {...inputFormItemLayout} label="Employee Type">
        {
          getFieldDecorator('employer', {
            initialValue: data.employer
          })(<Input disabled={readonly || managed} placeholder="i.e Contractor" />)
        }
      </FormItem>
      <FormItem {...inputFormItemLayout} label="Position Title">
        {
          getFieldDecorator('employee_position', {
            initialValue: data.employee_position
          })(<Input disabled={readonly || managed} />)
        }
      </FormItem>
      <FormItem {...inputFormItemLayout} label="Start Date">
        {
          getFieldDecorator('start_date', {
            initialValue: data.start_date
          })(<DatePicker disabled={readonly || managed} format="DD-MM-YYYY" disabledDate={startValue => (setUpperDateRange(startValue, data.end_date))} />)
        }
      </FormItem>
      <FormItem {...inputFormItemLayout} label="End Date">
        {
          getFieldDecorator('end_date', {
            initialValue: data.end_date
          })(<DatePicker disabled={readonly || managed} format="DD-MM-YYYY" disabledDate={endValue => (setLowerDateRange(endValue, data.start_date))} />)
        }
      </FormItem>
      <FormItem {...inputFormItemLayout} label="LMS Id">
        {
          getFieldDecorator('lms_id', {
            initialValue: data.lms_id
          })(<Input disabled={readonly} />)
        }
      </FormItem>
      <Divider>Supervisor Details</Divider>
      <FormItem {...inputFormItemLayout} label="Name">
        {
          getFieldDecorator('supervisor', {
            initialValue: data.supervisor
          })(<Input disabled={readonly || managed} />)
        }
      </FormItem>
      <FormItem {...inputFormItemLayout} label="Contact No.">
        {
          getFieldDecorator('supervisor_contact_number', {
            initialValue: data.supervisor_contact_number
          })(<Input disabled={readonly} />)
        }
      </FormItem>
      <FormItem {...inputFormItemLayout} label="Email">
        {
          getFieldDecorator('supervisor_email', {
            initialValue: data.supervisor_email,
            rules: [{
              type: 'email',
              message: 'Must be a valid E-mail.'
            }]
          })(<Input disabled={readonly || managed} />)
        }
      </FormItem>
      <Divider>Personal Details</Divider>
      <FormItem {...inputFormItemLayout} label="Suburb">
        {
          getFieldDecorator('suburb', {
            initialValue: data.suburb
          })(<Input disabled={readonly} />)
        }
      </FormItem>
      <FormItem {...inputFormItemLayout} label="State">
        {
          getFieldDecorator('state', {
            initialValue: data.state
          })(<Input disabled={readonly} />)
        }
      </FormItem>
      <FormItem {...inputFormItemLayout} label="Country">
        {
          getFieldDecorator('country', {
            initialValue: data.country
          })(<Input disabled={readonly} />)
        }
      </FormItem>
      <FormItem {...inputFormItemLayout} label="Email Address">
        {
          getFieldDecorator('personal_email_address', {
            initialValue: data.personal_email_address
          })(<Input disabled={readonly} />)
        }
      </FormItem>
      <FormItem {...inputFormItemLayout} label="Primary Contact No.">
        {
          getFieldDecorator('primary_phone_number', {
            initialValue: data.primary_phone_number
          })(<Input disabled={readonly} />)
        }
      </FormItem>
      <FormItem {...inputFormItemLayout} label="Secondary Contact No.">
        {
          getFieldDecorator('secondary_phone_number', {
            initialValue: data.secondary_phone_number
          })(<Input disabled={readonly} />)
        }
      </FormItem>
      <FormItem {...inputFormItemLayout} label="Home Phone No.">
        {
          getFieldDecorator('home_number', {
            initialValue: data.home_number
          })(<Input disabled={readonly} />)
        }
      </FormItem>

      <Divider>Emergency Contact</Divider>
      <FormItem {...inputFormItemLayout} label="Contact Name">
        {
          getFieldDecorator('emergency_contact_name', {
            initialValue: data.emergency_contact_name
          })(<Input disabled={readonly} />)
        }
      </FormItem>
      <FormItem {...inputFormItemLayout} label="Relationship">
        {
          getFieldDecorator('emergency_contact_relationship', {
            initialValue: data.emergency_contact_relationship
          })(<Input disabled={readonly} />)
        }
      </FormItem>
      <FormItem {...inputFormItemLayout} label="Home No.">
        {
          getFieldDecorator('emergency_contact_home', {
            initialValue: data.emergency_contact_home
          })(<Input disabled={readonly} />)
        }
      </FormItem>
      <FormItem {...inputFormItemLayout} label="Mobile No.">
        {
          getFieldDecorator('emergency_contact_mobile', {
            initialValue: data.emergency_contact_mobile
          })(<Input disabled={readonly} />)
        }
      </FormItem>
      <Divider>Attributes</Divider>
      <FormItem {...inputFormItemLayout} label="Roster order">
        {
          getFieldDecorator('order', {
            initialValue: data.order
          })(<Input disabled={readonly} />)
        }
      </FormItem>
      {/* The dagger (**) indicates this field feeds the Optimiser */}
      {optimiserEnabled && (
        <FormItem {...inputFormItemLayout} label="** Preferred Mentor">
          {getFieldDecorator('preferred_mentors', {
            initialValue: mentorIdsOf(data.preferred_mentors)
          })(
            <MentorRankList
              options={mentorSelectOptions}
              onSearch={fetchMentors}
              fetching={mentorFetching}
              currentUserId={data.user_id}
              disabled={readonly}
              // getFieldDecorator clones this element and supplies the real onChange; the
              // noop only satisfies the child's required propType at createElement time.
              onChange={_.noop}
            />
          )}
        </FormItem>
      )}
      {optimiserEnabled && (
        <FormItem {...(checkboxFormItemLayout || inputFormItemLayout)}>
          {getFieldDecorator('include_for_optimiser', {
            initialValue: data.include_for_optimiser !== false,
            valuePropName: 'checked'
          })(
            <Checkbox disabled={readonly || !canAdminOptimiser()}>
              Include for Optimiser
              {' '}
              <Tooltip title="This user should be included for use by the Optimiser">
                <Icon type="question-circle-o" />
              </Tooltip>
            </Checkbox>
          )}
        </FormItem>
      )}
      {optimiserEnabled && OPTIMISER_LEGEND}
    </Form>
  );
};

FormInput.propTypes = {
  canAdmin: PropTypes.bool,
  dataSource: PropTypes.object,
  form: PropTypes.object,
  checkboxFormItemLayout: PropTypes.object,
  inputFormItemLayout: PropTypes.object,
  person: PropTypes.object,
  onFieldsChange: PropTypes.func,
  onValuesChange: PropTypes.func
};

FormInput.defaultProps = {
  canAdmin: false,
  dataSource: {},
  form: {},
  checkboxFormItemLayout: {},
  inputFormItemLayout: {},
  person: {},
  onFieldsChange() {
  },
  onValuesChange() {
  }
};

const AccountInput = FormWrapper(Form.create({
  mapPropsToFields,
  onFieldsChange,
  onValuesChange
})(FormInput));

export default connect(mapStateToProps, null)(AccountInput);
